import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import { useCustomization } from '@/contexts/CustomizationContext';
import { authService } from '@/services/authService';
import { getDefaultHomePath } from '@/constants/modules';
import type { AuthUser } from '@/types';
import { Eye, EyeOff } from 'lucide-react';
import { Footer } from '@/components/layout/Footer';

const LOGO_ZEHMOTOCA = '/logozehmotoca.png';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const SYSTEM_NAME = 'PortalFly Zeh Motoca';

/* Cores do app mobile Zeh Motoca (theme.ts) */
const MOBILE_COLORS = {
  primary: '#FFC107',
  primaryDark: '#FFA000',
  primaryLight: '#FFECB3',
  background: '#FFFFFF',
  surfaceVariant: '#FAFAFA',
  text: '#000000',
  textSecondary: '#757575',
} as const;

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { customization } = useCustomization();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    try {
      const response = await authService.login(data);
      login(response.user, response.accessToken, response.refreshToken);
      toast.success('Login realizado com sucesso!');
      navigate(getDefaultHomePath(response.user as AuthUser));
    } catch (error) {
      toast.error('Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] dark:bg-[#0a0a0a]">
      <div className="flex flex-1 min-h-0">
      {/* Lado esquerdo: marca e título (gradiente amarelo do app mobile) */}
      <div
        className="hidden lg:flex lg:w-[50%] xl:w-[55%] flex-col justify-center px-12 xl:px-20 py-12 relative overflow-hidden"
        style={{ background: `linear-gradient(to bottom, ${MOBILE_COLORS.primary}, ${MOBILE_COLORS.primaryDark})` }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-32 right-32 w-96 h-96 rounded-full bg-white blur-3xl" />
        </div>
        <div className="relative opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-xl bg-black/10 backdrop-blur flex items-center justify-center overflow-hidden">
              <img
                src={customization.logoUrl || LOGO_ZEHMOTOCA}
                alt="Zeh Motoca"
                className="w-10 h-10 object-contain"
              />
            </div>
            <span className="text-black/80 text-sm font-medium">{customization.systemName || SYSTEM_NAME}</span>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-black tracking-tight mb-4">
            {SYSTEM_NAME}
          </h1>
          <p className="text-black/70 text-lg max-w-md leading-relaxed">
            Acesse o portal de tecnologias da Zeh Motoca para gerenciar cobranças, oficina, aplicativo e novas frentes do negócio.
          </p>
        </div>
      </div>

      {/* Lado direito: formulário de login (fundo branco/card como no mobile) */}
      <div className="w-full lg:w-[50%] xl:w-[45%] flex items-center justify-center px-6 sm:px-10 py-12 bg-[#FFFFFF] dark:bg-[#0a0a0a]">
        <div className="w-full max-w-md opacity-0 animate-slide-in-right" style={{ animationDelay: '0.15s' }}>
          <Card className="border border-[#E0E0E0] dark:border-[#212121] shadow-lg bg-[#FFFFFF] dark:bg-[#141414]">
            <CardHeader className="text-center pb-2 lg:hidden">
              <h1 className="text-2xl font-bold text-black dark:text-white">{SYSTEM_NAME}</h1>
            </CardHeader>
            <CardHeader className="pb-4 pt-6 sm:pt-8">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden p-1" style={{ backgroundColor: MOBILE_COLORS.primary }}>
                  <img
                    src={customization.logoUrl || LOGO_ZEHMOTOCA}
                    alt="Zeh Motoca"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <CardTitle className="text-xl font-bold text-center">Acesso ao Sistema</CardTitle>
              <CardDescription className="text-center">
                Entre com suas credenciais para continuar
              </CardDescription>
            </CardHeader>

            <CardContent className="pb-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  label="E-mail"
                  type="email"
                  placeholder="seu@email.com"
                  error={errors.email?.message}
                  {...register('email')}
                />

                <div className="relative">
                  <Input
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Sua senha"
                    error={errors.password?.message}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full mt-2 !bg-button-brand !text-button-brand-foreground shadow-button hover:shadow-button-hover hover:!bg-button-brand/90 hover:-translate-y-0.5"
                  loading={isLoading}
                >
                  Entrar no Sistema
                </Button>
                <p className="text-center text-xs text-muted-foreground mt-4">
                  <Link to="/cliente/login" className="underline hover:text-foreground">
                    Sou locatário (app)
                  </Link>
                  {' · '}
                  Ao entrar, você concorda com nossa{' '}
                  <Link to="/politica-de-privacidade" className="underline hover:text-foreground">
                    Política de Privacidade
                  </Link>
                  .
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};
